/**
 * SearchExecutions slice — generic, filter-driven execution search.
 *
 * searchExecutions(input): POST /executions/search via beaconApi; stores the raw
 * SearchExecutions summaries + total count. Callers supply the
 * SearchExecutionsInput (filters/pagination/sort). The bulk execution search
 * page uses it with filters.executionIds = [queried ids].
 *
 * A global search keeps each region's page separate in `regionResults`;
 * `executions` is every region's rows flattened, for single-region consumers.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ApiExecutionSummary, SearchExecutionsInput } from './searchExecutionsApi';
import { beaconApi } from '../../../services/beaconApi';
import { RootState } from '../../store';
import { resolveApiBase } from '../env/envState';
import {
  RegionPageResult, RegionSearchState, SearchFanOutResult, applyRegionPage, describeFailures,
  fanOutSearch, flattenPages, markRegionError, markRegionLoading, regionBases,
  regionSearchInitialState,
} from '../regionFanOut';

export interface SearchExecutionsState extends RegionSearchState<ApiExecutionSummary> {
  /** All regions' rows in one list. */
  executions: ApiExecutionSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  currentRequestId: string | null;
  /** Last search input; re-dispatched on region change. */
  lastInput: SearchExecutionsInput | null;
}

export const searchExecutionsInitialState: SearchExecutionsState = {
  executions: [],
  total: 0,
  loading: false,
  error: null,
  currentRequestId: null,
  lastInput: null,
  ...regionSearchInitialState,
};

export const searchExecutions = createAsyncThunk<
  SearchFanOutResult<ApiExecutionSummary>, SearchExecutionsInput,
  { state: RootState; rejectValue: string }
>('searchExecutions/run', async (input, { getState }) => {
  const run = async (apiBase: string) => {
    const response = await beaconApi.searchExecutions(input, apiBase);
    return { items: response.executions ?? [], total: response.totalExecutionsCount ?? 0 };
  };
  return fanOutSearch(regionBases(getState().env), run);
});

/** Fetch one region's page for the independent per-region bulk tables (merge-only). */
export const searchExecutionsRegion = createAsyncThunk<
  RegionPageResult<ApiExecutionSummary>, { input: SearchExecutionsInput; region: string },
  { state: RootState }
>('searchExecutions/region', async ({ input, region }, { getState }) => {
  const apiBase = resolveApiBase(getState().env.stage, region);
  const response = await beaconApi.searchExecutions(input, apiBase);
  return { region, items: response.executions ?? [], total: response.totalExecutionsCount ?? 0 };
});

const searchExecutionsSlice = createSlice({
  name: 'searchExecutions',
  initialState: searchExecutionsInitialState,
  reducers: {
    clearSearchExecutions: () => searchExecutionsInitialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchExecutions.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
        state.lastInput = action.meta.arg;
      })
      .addCase(searchExecutions.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        const { regionResults, searchedRegions, errors } = action.payload;
        const flat = flattenPages(regionResults);
        state.loading = false;
        state.regionResults = regionResults;
        state.searchedRegions = searchedRegions;
        state.regionErrors = errors;
        state.executions = flat.items;
        state.total = flat.total;
        state.error = flat.items.length === 0 && errors.length > 0
          ? `No region returned executions. ${describeFailures(errors)}`
          : null;
      })
      .addCase(searchExecutions.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to search executions';
      })
      // Per-region (independent table) query.
      .addCase(searchExecutionsRegion.pending, (state, action) => {
        markRegionLoading(state, action.meta.arg.region, action.meta.requestId);
      })
      .addCase(searchExecutionsRegion.fulfilled, (state, action) => {
        if (applyRegionPage(state, action.payload, action.meta.requestId)) {
          const flat = flattenPages(state.regionResults);
          state.executions = flat.items;
          state.total = flat.total;
        }
      })
      .addCase(searchExecutionsRegion.rejected, (state, action) => {
        if (markRegionError(state, action.meta.arg.region, action.meta.requestId,
          action.error.message || 'Failed to search executions')) {
          const flat = flattenPages(state.regionResults);
          state.executions = flat.items;
          state.total = flat.total;
        }
      });
  },
});

export const { clearSearchExecutions } = searchExecutionsSlice.actions;
export default searchExecutionsSlice.reducer;
